import React, {useEffect, useMemo} from 'react';
import {
  PrismTransaction,
  TransactionTeam,
  useAddJarvisDataMutation, useAddTransactionJarvisDataMutation,
  useLazyGetPrismTransactionByIdQuery
} from '@repo/services';
import { createFormDataObject, useAgLazyQuery, useAgMutation } from '@repo/utils';
import {useForm} from "@mantine/form";
import {Button, Modal, TextInput, Tooltip} from "@mantine/core";
import {IconCircleX} from "@tabler/icons-react";
import {useDisclosure} from "@mantine/hooks";
import {notifications} from "@mantine/notifications";
import SubtleButton from '../../SubtleButton';

interface AddJarvisIdModalProps {
    transactionId: string | undefined;
}

/**
 * Renders a button that opens a modal for adding or editing the Jarvis ID/name associated with a transaction, pre-filling the form with any existing values and saving via the transaction Jarvis data mutation.
 *
 * @param transactionId - The ID of the transaction whose Jarvis data is being added or edited.
 */
function AddJarvisIdModal({ transactionId }: AddJarvisIdModalProps) {
    const [opened, { open, close }] = useDisclosure(false);

    const [transactionToDisplay] = useAgLazyQuery<PrismTransaction>(
      transactionId && parseFloat(transactionId) !== 0 ? transactionId : undefined,
      useLazyGetPrismTransactionByIdQuery
    )

    const jarvisData = useMemo(() => {
      if (transactionToDisplay) {
        return {
          jarvisId: transactionToDisplay?.jarvisId,
          jarvisName: transactionToDisplay?.jarvisName,
        }
      }
    }, [transactionToDisplay]);

    const form = useForm({
        initialValues: {
            jarvisId: '',
            jarvisName: '',
        },
    });

    const handleClose = () => {
        form.reset();
        close();
    }

    const { trigger: triggerAddJarvisData, isLoading: isSaving } = useAgMutation<PrismTransaction>(
        useAddTransactionJarvisDataMutation,
        () => {
            notifications.show({
                title: "Jarvis Data Saved",
                message: `Jarvis data for acquisition ${transactionId} was saved successfully!`
            });
            handleClose();
        }
    );

    const saveForm = (values) => {
        if (transactionId) {
            triggerAddJarvisData({
                transactionId: +transactionId,
                payload: values
            });
        }
    };

    const buttonText = useMemo(() => jarvisData?.jarvisId || jarvisData?.jarvisName ? "Edit Jarvis ID" : "Add Jarvis ID", [jarvisData?.jarvisId, jarvisData?.jarvisName])

    useEffect(() => {
        if (jarvisData?.jarvisId || jarvisData?.jarvisName) {
            form.setFieldValue('jarvisId', jarvisData?.jarvisId as any);
            form.setFieldValue('jarvisName', jarvisData?.jarvisName as any);
        }
    },[jarvisData?.jarvisId, jarvisData?.jarvisName]);

    return (
        <>
            <Modal
                title='Add Jarvis ID'
                opened={opened}
                onClose={handleClose}
            >
                <form onSubmit={form.onSubmit((values) => saveForm(values))}>
                    <TextInput
                        label="Jarvis ID (Optional)"
                        name="jarvisId"
                        {...form.getInputProps('jarvisId')}
                    />

                    <TextInput
                        label="Jarvis Name (Optional)"
                        name="jarvisName"
                        {...form.getInputProps('jarvisName')}
                    />

                    <Button mt={8} type="submit" loading={isSaving}>Submit</Button>
                </form>
            </Modal>

            <Tooltip label={buttonText}>
                <SubtleButton buttonColor={'var(--mantine-color-blue-filled)'} text={buttonText} mr={8} onClick={open} />
            </Tooltip>
        </>
    );
}

export default AddJarvisIdModal;
